import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Save, Trash2 } from "lucide-react";
import { api, errMsg, useContent } from "@/lib/api";
import { SCHEMAS, sectionsToText, textToSections } from "@/pages/admin/schemas";

function Field({ f, item, set }) {
  const [key, label, kind] = f;
  const v = item[key];
  if (kind === "bool") return (
    <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!v} onChange={(e) => set(key, e.target.checked)} className="h-4 w-4 accent-sky-400" data-testid={`edit-${key}`} />{label}</label>
  );
  const common = { className: "field", "data-testid": `edit-${key}` };
  let input;
  if (kind === "area") input = <textarea rows={3} {...common} value={v || ""} onChange={(e) => set(key, e.target.value)} />;
  else if (kind === "lines") input = <textarea rows={4} {...common} value={(v || []).join("\n")} onChange={(e) => set(key, e.target.value.split("\n"))} />;
  else if (kind === "sections") input = <SectionsField v={v} set={(x) => set(key, x)} />;
  else input = <input type={kind === "number" ? "number" : "text"} {...common} dir={kind === "image" ? "ltr" : undefined} value={v ?? ""} onChange={(e) => set(key, e.target.value)} />;
  return (
    <label className={`block ${["area", "lines", "sections"].includes(kind) ? "sm:col-span-2" : ""}`}>
      <span className="mb-1.5 block text-xs text-slate-400">{label}</span>
      <div className="flex items-center gap-3">{kind === "image" && v && <img src={v} alt="" className="h-11 w-16 shrink-0 rounded-lg object-cover" />}{input}</div>
    </label>
  );
}

function SectionsField({ v, set }) {
  const [txt, setTxt] = useState(sectionsToText(v));
  return (
    <div>
      <textarea rows={14} className="field font-mono2 !text-[13px] leading-7" value={txt} onChange={(e) => { setTxt(e.target.value); set(textToSections(e.target.value)); }} data-testid="edit-sections" />
      <p className="mt-1 text-[11px] text-slate-500">اكتب عنوان القسم بعد ## وكل قانون بسطر يبدأ بـ -</p>
    </div>
  );
}

function ItemCard({ item, i, n, schema, update, move, remove }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass rounded-2xl" data-testid={`edit-item-${i}`}>
      <div className="flex items-center gap-2 p-3">
        <button onClick={() => setOpen(!open)} className="flex flex-1 items-center gap-3 text-right text-sm font-semibold" data-testid={`edit-item-toggle-${i}`}>
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />{schema.title(item) || "عنصر جديد"}
        </button>
        <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded-lg p-1.5 text-slate-400 hover:text-white disabled:opacity-30" aria-label="فوق"><ArrowUp size={15} /></button>
        <button disabled={i === n - 1} onClick={() => move(i, 1)} className="rounded-lg p-1.5 text-slate-400 hover:text-white disabled:opacity-30" aria-label="تحت"><ArrowDown size={15} /></button>
        <button onClick={() => remove(i)} className="rounded-lg p-1.5 text-slate-500 hover:text-rose-400" aria-label="حذف" data-testid={`edit-item-delete-${i}`}><Trash2 size={15} /></button>
      </div>
      {open && <div className="grid gap-4 border-t border-sky-400/10 p-5 sm:grid-cols-2">{schema.fields.map((f) => <Field key={f[0]} f={f} item={item} set={(k, val) => update(i, { ...item, [k]: val })} />)}</div>}
    </div>
  );
}

function Editor({ type }) {
  const schema = SCHEMAS[type];
  const { data } = useContent(type);
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) { setItems(data); setDirty(false); } }, [data]);
  const change = (next) => { setItems(next); setDirty(true); };
  const update = (i, it) => change(items.map((x, j) => (j === i ? it : x)));
  const move = (i, d) => { const a = [...items]; [a[i], a[i + d]] = [a[i + d], a[i]]; change(a); };
  const remove = (i) => window.confirm("تحذف هذا العنصر؟") && change(items.filter((_, j) => j !== i));
  const add = () => change([...items, {}]);
  const save = async () => {
    setBusy(true);
    const clean = items.map((it) => Object.fromEntries(Object.entries(it).map(([k, v]) => [k, Array.isArray(v) && typeof v[0] !== "object" ? v.map((s) => String(s).trim()).filter(Boolean) : v])));
    try { const r = await api.put(`/admin/content/${type}`, { items: clean }); qc.setQueryData(["content", type], r.data.items); setDirty(false); toast.success("انحفظ التعديل"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div>
      <div className="sticky top-[72px] z-10 mb-4 flex items-center justify-between gap-3 rounded-2xl border border-sky-400/15 bg-[#070b14]/90 p-3 backdrop-blur">
        <span className="text-sm text-slate-400">{items.length} عنصر{dirty && <span className="ms-2 text-amber-300">· فيه تعديلات ما انحفظت</span>}</span>
        <div className="flex gap-2">
          <button onClick={add} className="btn-ghost !py-2" data-testid="edit-add-btn"><Plus size={16} />إضافة</button>
          <button onClick={save} disabled={!dirty || busy} className="btn-ice !py-2" data-testid="edit-save-btn"><Save size={16} />{busy ? "يحفظ..." : "حفظ"}</button>
        </div>
      </div>
      <div className="space-y-3">{items.map((it, i) => <ItemCard key={it.id || `new-${i}`} item={it} i={i} n={items.length} schema={schema} update={update} move={move} remove={remove} />)}</div>
    </div>
  );
}

export default function AdminContent() {
  const [type, setType] = useState("news");
  return (
    <div className="grid gap-6 lg:grid-cols-[200px_1fr]">
      <div className="flex flex-wrap gap-2 lg:flex-col" data-testid="admin-content-types">
        {Object.entries(SCHEMAS).map(([k, s]) => (
          <button key={k} onClick={() => setType(k)} className={`rounded-xl border px-4 py-2.5 text-right text-sm transition-colors ${type === k ? "border-sky-400 bg-sky-400/15" : "border-sky-400/10 text-slate-400 hover:text-white"}`} data-testid={`admin-content-${k}`}>{s.label}</button>
        ))}
      </div>
      <Editor key={type} type={type} />
    </div>
  );
}
